import type { MouseEventHandler, ReactNode } from "react";

import { cn } from "@/utils/cn";

// Figma: https://www.figma.com/design/72p7BRSjceW2TlzRIHHRAB/Evvy-Core-Design-System?node-id=195-591

export const CTA_VARIANTS = [
  "primary",
  "secondary",
  "tertiary-black",
  "tertiary-white",
] as const;
export const CTA_SIZES = ["mini", "mobile", "default", "desktop"] as const;
export const CTA_ICON_POSITIONS = ["left", "right"] as const;

type CTAVariant = (typeof CTA_VARIANTS)[number];
type CTASize = (typeof CTA_SIZES)[number];
type CTAIconPosition = (typeof CTA_ICON_POSITIONS)[number];

type CTAProps = {
  children: ReactNode;
  /** tertiary-white is for dark backgrounds. */
  variant?: CTAVariant;
  size?: CTASize;
  icon?: ReactNode;
  iconPosition?: CTAIconPosition;
  /** Renders an `<a>` instead of a `<button>`. */
  href?: string;
  target?: string;
  type?: "button" | "submit";
  /** Figma "deactivated". Overrides the variant's colors. */
  disabled?: boolean;
  /** Hides the label behind a spinner and ignores clicks. The name stays available to assistive tech. */
  loading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /** Layout only. A repeated look is a variant. */
  className?: string;
};

const BASE = cn(
  // `unset` opts out of app.css `button { font-size: 12px }`, which otherwise beats these sizes.
  "unset relative inline-flex items-center justify-center gap-ds-space-03 whitespace-nowrap border border-transparent text-center",
  "uppercase"
);

const VARIANTS: Record<CTAVariant, string> = {
  primary: "bg-ds-ui-elements-evvy-blue text-ds-ui-elements-black",
  secondary: "bg-ds-ui-elements-black text-ds-ui-elements-white",
  "tertiary-black":
    "border-ds-ui-elements-black bg-ds-ui-elements-white text-ds-ui-elements-black",
  "tertiary-white": "border-ds-ui-elements-white text-ds-ui-elements-white",
};

// default is Figma's size=mobile below md: and size=desktop from md:. mobile is size=mobile at
// every width, for desktop frames that place the mobile button (a modal); desktop is
// size=desktop at every width, for mobile frames that place it (Fertility-V My Plan's care CTA).
// mini has no desktop frame. Heights and widths are fixed frames in Figma, with no variable
// (EVY-436).
const SIZES: Record<CTASize, string> = {
  mini: "h-[40px] min-w-[120px] px-ds-space-05 text-ds-header-subheader",
  mobile: "h-[56px] min-w-[220px] px-ds-space-06 text-ds-header-subheader",
  default: cn(
    "ds-header-subheader h-[56px] min-w-[220px] px-ds-space-06",
    "md:h-[72px] md:w-[320px] md:min-w-[320px] md:px-ds-space-06-desktop"
  ),
  desktop:
    "h-[72px] w-[320px] min-w-[320px] px-ds-space-06-desktop text-ds-header-subheader-desktop",
};

// deactivated is its own opaque look. app.css `[disabled] { opacity: 0.5 }` loads after the
// utilities and ties a plain opacity-100, so the disabled: variant's extra specificity is needed
const DISABLED =
  "cursor-not-allowed border-ds-line-light-on-creme bg-ds-ui-elements-deactivated text-ds-text-light-on-white disabled:opacity-100";

// Button, or an anchor when `href` is set, so there is no single ref to forward. See ui/README.md.
export const CTA = ({
  children,
  variant = "primary",
  size = "default",
  icon,
  iconPosition = "left",
  href,
  target,
  type = "button",
  disabled = false,
  loading = false,
  onClick,
  className,
}: CTAProps) => {
  const classNames = cn(
    BASE,
    VARIANTS[variant],
    SIZES[size],
    disabled && DISABLED,
    className
  );
  const iconNode = icon ? (
    // decorative: the label alone names the button
    <span aria-hidden className="flex shrink-0 [&_svg]:size-ds-icon-base">
      {icon}
    </span>
  ) : null;
  const content = (
    <>
      <span
        className={cn(
          "inline-flex items-center gap-ds-space-03",
          loading && "opacity-0"
        )}
      >
        {iconPosition === "left" && iconNode}
        {children}
        {iconPosition === "right" && iconNode}
      </span>
      {loading ? (
        <span
          aria-hidden
          className="absolute size-ds-icon-base animate-spin rounded-ds-corner-full border-2 border-current border-t-transparent"
        />
      ) : null}
    </>
  );
  const inert = disabled || loading;

  if (href && !inert) {
    return (
      <a
        href={href}
        target={target}
        rel={target === "_blank" ? "noreferrer" : undefined}
        onClick={onClick}
        className={classNames}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      // Loading uses type="button" so a submit CTA cannot submit and the spinner
      // keeps its color. An inert link renders here too, with no href.
      type={loading ? "button" : type}
      disabled={disabled}
      aria-disabled={loading && !disabled ? true : undefined}
      aria-busy={loading || undefined}
      onClick={inert ? undefined : onClick}
      className={classNames}
    >
      {content}
    </button>
  );
};
